import { useState, useCallback } from 'react';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../utils/googleMaps';
import { MapPin, Navigation, Loader2 } from 'lucide-react';

const containerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '0.75rem'
};

const defaultCenter = {
  lat: 42.7339,
  lng: 25.4858 // Bulgaria - default location
};

const FarmLocationPicker = ({ value, onChange }) => {
  const { isLoaded } = useJsApiLoader(GOOGLE_MAPS_CONFIG);

  const [map, setMap] = useState(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  const position = value.lat && value.lng 
    ? { lat: parseFloat(value.lat), lng: parseFloat(value.lng) }
    : null;

  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMapClick = (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    onChange({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        onChange({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
        
        if (map) {
          map.panTo({ lat, lng });
          map.setZoom(15);
        }
        setIsGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please click on the map to set location.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  if (!isLoaded) {
    return (
      <div className="h-[300px] bg-stone-100 rounded-xl flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium">Farm Location</label>
        <button
          type="button"
          onClick={handleUseMyLocation}
          disabled={isGettingLocation}
          className="flex items-center gap-2 text-sm text-[rgb(var(--primary))] hover:underline disabled:opacity-50"
        >
          {isGettingLocation ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Navigation className="w-4 h-4" />
          )}
          Use my location
        </button>
      </div>

      <div className="border rounded-xl overflow-hidden">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={position || defaultCenter}
          zoom={position ? 15 : 6}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            mapTypeId: 'hybrid',
            mapTypeControl: true,
            streetViewControl: false,
            fullscreenControl: false
          }}
        >
          {position && (
            <Marker
              position={position}
              draggable={true}
              onDragEnd={(e) => {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                onChange({ lat: lat.toFixed(6), lng: lng.toFixed(6) });
              }}
            />
          )}
        </GoogleMap>
      </div>

      <div className="flex items-center gap-2 text-sm text-stone-500">
        <MapPin className="w-4 h-4" />
        {position ? (
          <span>
            Selected: {parseFloat(value.lat).toFixed(4)}°N, {parseFloat(value.lng).toFixed(4)}°E
          </span>
        ) : (
          <span>Click on the map or use "My Location" to set farm coordinates</span>
        )}
      </div>

      {/* Hidden inputs for form validation */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs text-stone-400 mb-1">Latitude</label>
          <input
            type="number"
            step="any"
            value={value.lat}
            onChange={(e) => onChange({ ...value, lat: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg bg-stone-50 text-sm focus:ring-2 focus:ring-[rgb(var(--primary))] outline-none"
            placeholder="Click map or enter manually"
            required
          />
        </div>
        <div>
          <label className="block text-xs text-stone-400 mb-1">Longitude</label>
          <input
            type="number"
            step="any"
            value={value.lng}
            onChange={(e) => onChange({ ...value, lng: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg bg-stone-50 text-sm focus:ring-2 focus:ring-[rgb(var(--primary))] outline-none"
            placeholder="Click map or enter manually"
            required
          />
        </div>
      </div>
    </div>
  );
};

export default FarmLocationPicker;
